import { cookieChannel } from '@fingoti/auth-js/org-sync';

let _auth = null;

export const fingotiAuthConfig = {
    authBaseUrl: process.env.REACT_APP_AUTH_BASE_URL,
    clientId: process.env.REACT_APP_AUTH_CLIENT_ID,
    redirectUri: process.env.REACT_APP_AUTH_REDIRECT_URI,
    postLogoutRedirectUri: process.env.REACT_APP_ACCOUNT_URL,
    // The refresh-token family was revoked: the SDK has already wiped local
    // state by the time this fires. Re-authenticate, returning to wherever the
    // user was. This covers the path where getAccessToken() throws
    // FingotiUnrecoverableError before a request ever reaches the resource API.
    onUnrecoverable: () => {
        reauthenticate();
    },
};

// How recently a re-login must have happened for the next auth failure to count
// as a loop. Long enough to span a hosted-login round-trip (incl. silent SSO),
// short enough that a genuine later expiry still triggers a fresh redirect.
const RELOGIN_WINDOW_MS = 15000;
const RELOGIN_KEY = 'fingoti.portal.lastRelogin';

/**
 * Re-authenticate via a full login redirect, returning the user to the current
 * page. Guards against redirect loops: if we already bounced through the auth
 * server within RELOGIN_WINDOW_MS and are still being rejected, a fresh token
 * isn't fixing this (scope/audience misconfig, resource outage), so we refuse to
 * redirect and return false to let the caller surface an error instead.
 *
 * The marker is deliberately NOT cleared on a loop detection: a single page load
 * fires several requests that can all 401 in the same tick, and clearing here
 * would re-arm the guard for the next parallel caller — which would redirect
 * again, thrashing the marker and looping forever. The marker instead resets
 * only when a request actually succeeds (see resetReauthGuard) or ages out of
 * the window. Net effect: at most one redirect per RELOGIN_WINDOW_MS.
 *
 * @returns {boolean} true if a redirect was triggered, false if a loop was detected.
 */
export const reauthenticate = () => {
    const last = Number(sessionStorage.getItem(RELOGIN_KEY) || 0);
    const now = Date.now();

    if (last && now - last < RELOGIN_WINDOW_MS) {
        return false;
    }

    sessionStorage.setItem(RELOGIN_KEY, String(now));

    login({ returnTo: window.location.pathname + window.location.search });

    return true;
};

/**
 * Clear the re-login loop guard. Called on any successful response: a 2xx proves
 * auth is healthy, so a later genuine expiry should be free to redirect again
 * without waiting out the window.
 */
export const resetReauthGuard = () => {
    sessionStorage.removeItem(RELOGIN_KEY);
};

export const setAuth = (auth) => {
    _auth = auth;
};

export const getAccessToken = async (opts) => {
    if (!_auth) {
        throw new Error('FingotiAuth not initialised yet');
    }
    return _auth.getAccessToken(opts);
};

export const login = (opts) => {
    if (!_auth) return Promise.reject(new Error('FingotiAuth not initialised yet'));
    return _auth.login(opts);
};

export const logout = (opts) => {
    if (!_auth) return Promise.reject(new Error('FingotiAuth not initialised yet'));
    return _auth.logout(opts);
};

// Cross-subdomain org-context sharing. The chain of org ids (never tokens) is
// written to a cookie on the registrable domain so a switch / act-as in any
// Fingoti app (account, portal, …) follows the user into the others — each app
// re-derives its own tokens by replaying the exchanges. Created once at module
// scope: useOrgContextSync treats it as an effect dependency, so the identity
// must stay stable across renders.
//
// Domain must be the registrable domain (leading dot) shared by the sibling
// apps. DEV/STAGING live under *.fingoti.net, PROD under *.fingoti.com. LOCAL
// has no shared domain (localhost), so the cookie is host-only — sharing is
// effectively off and Secure is dropped for http://localhost.
const _envString = process.env.REACT_APP_ENVIROMENT_STRING;
const _contextCookieDomain =
    _envString === 'PROD' ? '.fingoti.com'
    : _envString === 'DEV' ? '.fingoti.net'
    : undefined;

export const orgContextChannel = cookieChannel({
    domain: _contextCookieDomain,
    secure: _envString !== 'LOCAL',
});
